// Renaming a board by hand (Fixes 9, 28 Oct), whatever its kind: the board in view is renamed on its title, in the
// board pill (a double-click on its name, Rename in the board menu, the command bar); any other board, or any board
// on a phone or from All boards, in a small dialog with its name. The boards list keeps its own rename in the row.

import { useEffect, useRef, useState } from "react";
import { create } from "zustand";
import { Pencil } from "lucide-react";
import { BOARD_NAME_MAX, cleanBoardName } from "../../shared/boardNames";
import type { Board, ID } from "../../shared/types";
import { Dialog } from "../chrome/ui";
import { updateBoardById } from "../state/actions";
import { get, useStore } from "../state/store";

/** The board being renamed, and whether on its title in the pill (inline) or in the dialog. */
export const useRenaming = create<{ id: ID | null; inline: boolean }>(() => ({ id: null, inline: false }));

/** Rename a board: on its title when it is the one in view on a computer, else in the dialog. An archived board is read only. */
export function renameBoard(id: ID) {
  const s = get();
  if (!s.boards.some((b) => b.id === id)) return;
  // in full screen's bar the name is the same one; elsewhere full screen and hidden buttons have no name to type in
  const name = document.querySelector(s.presenting ? ".fs-bar .board-pill-name" : ".board-pill-name");
  const inline = id === s.boardId && !s.mobile && s.panel !== "boards" && (s.presenting || !s.focusMode) && !!name;
  useRenaming.setState({ id, inline });
}

export function stopRenaming() {
  useRenaming.setState({ id: null, inline: false });
}

/** The name typed, saved when it is a new one, as one step that undo takes back. */
export function saveBoardName(board: Board, raw: string) {
  const v = cleanBoardName(raw);
  if (v && v !== board.name) updateBoardById(board.id, { name: v }, "Rename board");
}

/** The name in the board pill, as a field: Enter or a click away keeps it, Esc leaves it as it was. */
export function PillRename({ board }: { board: Board }) {
  const ref = useRef<HTMLInputElement>(null);
  // given focus once whatever opened it (a menu, the command bar) has closed and handed focus back
  useEffect(() => {
    const t = setTimeout(() => {
      ref.current?.focus();
      ref.current?.select();
    }, 30);
    return () => clearTimeout(t);
  }, []);
  return (
    <input
      ref={ref}
      autoFocus
      className="board-pill-rename"
      defaultValue={board.name}
      maxLength={BOARD_NAME_MAX}
      aria-label={`Rename ${board.name}`}
      size={Math.max(8, Math.min(32, board.name.length + 2))}
      onFocus={(e) => e.currentTarget.select()}
      onBlur={(e) => {
        saveBoardName(board, e.currentTarget.value);
        stopRenaming();
      }}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") {
          e.currentTarget.value = board.name;
          e.currentTarget.blur();
        }
      }}
    />
  );
}

/** The dialog, for a board renamed anywhere but its own title. */
export function RenameHost() {
  const { id, inline } = useRenaming();
  const board = useStore((s) => (id ? s.boards.find((b) => b.id === id) : undefined));
  if (!id || inline || !board) return null;
  return <RenameDialog key={id} board={board} />;
}

function RenameDialog({ board }: { board: Board }) {
  const [name, setName] = useState(board.name);
  const ok = !!cleanBoardName(name);
  const save = () => {
    if (!ok) return;
    saveBoardName(board, name);
    stopRenaming();
  };
  return (
    <Dialog
      open
      onClose={stopRenaming}
      title="Rename board"
      icon={<Pencil size={16} />}
      width={420}
      className="rename-board"
      footer={
        <>
          <button className="btn" onClick={stopRenaming}>
            Cancel
          </button>
          <button className="btn btn-primary" disabled={!ok} onClick={save}>
            Save
          </button>
        </>
      }
    >
      <label className="field">
        <span className="field-label">Name</span>
        <input
          autoFocus
          value={name}
          maxLength={BOARD_NAME_MAX}
          aria-label="Board name"
          onFocus={(e) => e.currentTarget.select()}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              save();
            }
          }}
        />
      </label>
    </Dialog>
  );
}
